import { NetworkStatus, useQuery, type WatchQueryFetchPolicy } from "@apollo/client";
import { gql } from "generated-graphql";
import { useDebounce } from "hooks/useDebounce";
import { useTenant } from "hooks/useTenant";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const THREADS_PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;
const PREVIEW_EXCERPT_MAX_CHARS = 88;

const GET_SCOUT_CONVERSATIONS = gql(`
  query GetScoutConversations($tenantId: ID!, $search: String, $page: Int, $pageSize: Int) {
    getScoutConversations(tenantId: $tenantId, search: $search, page: $page, pageSize: $pageSize) {
      count
      items {
        id
        title
        previewExcerpt
        updatedAt
        status
        writeMode
      }
    }
  }
`);

export type ScoutThreadListItem = {
  id: string;
  title?: string;
  previewExcerpt?: string;
  updatedAt: string;
};

function normalizePreviewExcerpt(previewExcerpt: string | null | undefined): string | undefined {
  const trimmed = previewExcerpt?.replace(/\s+/g, " ").trim();
  if (!trimmed) {
    return undefined;
  }

  if (trimmed.length <= PREVIEW_EXCERPT_MAX_CHARS) {
    return trimmed;
  }

  return `${trimmed.slice(0, PREVIEW_EXCERPT_MAX_CHARS - 1).trimEnd()}...`;
}

export function formatRelativeConversationTime(updatedAt: string): string {
  const now = DateTime.now();
  const timestamp = DateTime.fromISO(updatedAt);

  if (!timestamp.isValid) {
    return "";
  }

  const secondsAgo = now.diff(timestamp, "seconds").seconds;
  if (secondsAgo < 60) {
    return "now";
  }

  const minutesAgo = now.diff(timestamp, "minutes").minutes;
  if (minutesAgo < 10) {
    return "a few minutes ago";
  }

  if (minutesAgo < 60) {
    return (
      timestamp.toRelative({
        base: now,
        unit: "minutes",
        round: true,
      }) ?? "less than an hour ago"
    );
  }

  const hoursAgo = now.diff(timestamp, "hours").hours;
  if (hoursAgo < 24) {
    return (
      timestamp.toRelative({
        base: now,
        round: true,
      }) ?? "today"
    );
  }

  return timestamp.toRelativeCalendar({ base: now }) ?? timestamp.toFormat("LLL d");
}

export function useScoutThreadsSearch({
  enabled,
  searchInput: controlledSearchInput,
  onSearchInputChange,
  pageSize = THREADS_PAGE_SIZE,
  limit,
  fetchPolicy = "network-only",
}: {
  enabled: boolean;
  searchInput?: string;
  onSearchInputChange?: (value: string) => void;
  pageSize?: number;
  /**
   * When set, caps the results to this many threads and disables the
   * load/fetch-more behavior entirely.
   */
  limit?: number;
  /**
   * Defaults to `network-only` so search results and the dialog always
   * reflect the latest server ordering. In-panel recents pass
   * `cache-and-network` for an instant paint from cache.
   */
  fetchPolicy?: WatchQueryFetchPolicy;
}) {
  const isLimited = limit != null;
  const effectivePageSize = limit ?? pageSize;
  const { tenantId } = useTenant();
  const [internalSearchInput, setInternalSearchInput] = useState("");
  const searchInput = controlledSearchInput ?? internalSearchInput;
  const setSearchInput = onSearchInputChange ?? setInternalSearchInput;
  const trimmedSearch = searchInput.trim();
  const [debouncedSearch] = useDebounce(trimmedSearch, SEARCH_DEBOUNCE_MS);
  const isSearchMode = trimmedSearch.length > 0;
  const search = debouncedSearch.length > 0 ? debouncedSearch : undefined;
  const nextPageRef = useRef(0);
  const fetchMoreRequestIdRef = useRef(0);
  const isFetchingMoreRef = useRef(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  useEffect(
    function resetPaginationState() {
      nextPageRef.current = 0;
      fetchMoreRequestIdRef.current += 1;
      isFetchingMoreRef.current = false;
      setIsLoadingMore(false);
    },
    [enabled, effectivePageSize, search]
  );

  const { data, error, loading, fetchMore, networkStatus } = useQuery(GET_SCOUT_CONVERSATIONS, {
    skip: !enabled || !tenantId,
    fetchPolicy,
    notifyOnNetworkStatusChange: true,
    variables: tenantId
      ? {
          tenantId,
          search,
          page: 0,
          pageSize: effectivePageSize,
        }
      : undefined,
  });

  const items = useMemo(() => {
    const mapped = (data?.getScoutConversations.items ?? []).map((item) => ({
      id: item.id,
      title: item.title?.trim() ? item.title : undefined,
      previewExcerpt: normalizePreviewExcerpt(item.previewExcerpt),
      updatedAt: item.updatedAt,
    }));

    return limit != null ? mapped.slice(0, limit) : mapped;
  }, [data, limit]);

  const totalCount = data?.getScoutConversations.count ?? 0;
  const hasMore = !isLimited && items.length < totalCount;
  const isInitialLoading =
    loading && items.length === 0 && networkStatus !== NetworkStatus.fetchMore;

  const loadMore = useCallback(async () => {
    if (isLimited || !enabled || !tenantId || !hasMore || isFetchingMoreRef.current) {
      return;
    }

    const requestId = fetchMoreRequestIdRef.current;
    const nextPage = nextPageRef.current + 1;

    nextPageRef.current = nextPage;
    isFetchingMoreRef.current = true;
    setIsLoadingMore(true);

    try {
      await fetchMore({
        variables: {
          tenantId,
          search,
          page: nextPage,
          pageSize: effectivePageSize,
        },
      });
    } catch {
      if (fetchMoreRequestIdRef.current === requestId) {
        nextPageRef.current = nextPage - 1;
      }
    } finally {
      if (fetchMoreRequestIdRef.current === requestId) {
        isFetchingMoreRef.current = false;
        setIsLoadingMore(false);
      }
    }
  }, [effectivePageSize, enabled, fetchMore, hasMore, isLimited, search, tenantId]);

  return {
    searchInput,
    setSearchInput,
    heading: isSearchMode ? "Search results" : "Recents",
    items,
    isInitialLoading,
    isLoadingMore,
    hasMore,
    loadMore,
    emptyMessage: isSearchMode ? "No threads found." : "No recent threads.",
    errorMessage: error ? "Unable to load threads." : undefined,
  };
}
